import { MessagesSquare, AlertTriangle } from 'lucide-react';
import { FC, useRef, useEffect, useState } from 'react';
import { Title1, Text, Divider, tokens, Button, Switch, Dropdown, Option, Input, Textarea, Link, Tooltip, mergeClasses } from '@fluentui/react-components';
import { HorizontalSeparatorLine } from '../Common/HorizontalSeparatorLine';
import SyButton from '../Common/SyButton';
import { useIsMobile } from '../../Hooks/useResponsive';

// Types for supported languages and features
interface SupportedLanguage {
    locale: string;
    displayLanguage: string;
}

interface VoiceEntryType {
    key: string;
    text: string;
}

// Props interface for ConversationSettingsView
interface ConversationSettingsViewProps {
    classes: Record<string, string>;
    t: (key: string, defaultValue?: string) => string;
    DisenrollFromPlatformAvailable: boolean;
    selectedLanguage: string;
    speechToTextSupportedLanguages: SupportedLanguage[];
    voiceEntryType: string;
    voiceEntryTypes: VoiceEntryType[];
    autoSendNumSecsInput: string;
    autoSendNumSecsError: string;
    handleClearConversations: () => void;
    isClearingConversations: boolean;
    handleDisenroll: () => void;
    onUpdateUserPreferencesLanguage: (locale: string) => void;
    onUpdateVoiceEntryType: (entryType: string) => void;
    onUpdateAutoSendNumSecs: (numSecs: number) => void;
    validateAutoSendNumSecs: (value: string) => boolean;
    setAutoSendNumSecsInput: (value: string) => void;
    showLanguageSelectionInPrompt: boolean;
    onUpdateShowLanguageSelectionInPrompt: (show: boolean) => void;
}

const ConversationSettingsView: FC<ConversationSettingsViewProps> = (props) => {
    const MAX_AUTO_SEND_SECONDS = 5;
    const isMobile = useIsMobile();
    const [localShowVisualEditor, setLocalShowVisualEditor] = useState(true);

    const {
        classes,
        t,
        DisenrollFromPlatformAvailable,
        selectedLanguage,
        speechToTextSupportedLanguages,
        voiceEntryType,
        voiceEntryTypes,
        autoSendNumSecsInput,
        autoSendNumSecsError,
        handleClearConversations,
        isClearingConversations,
        handleDisenroll,
        onUpdateUserPreferencesLanguage,
        onUpdateVoiceEntryType,
        onUpdateAutoSendNumSecs,
        validateAutoSendNumSecs,
        setAutoSendNumSecsInput
    } = props;

    return (
        <div>
            <div className="titleSection">
                <div className="titleSection-content titleSection-content-indented">
                    <Title1 className="settingsTitle">
                        {t('settings.conversations.title', 'Chats')}
                    </Title1>
                </div>
            </div>

            <HorizontalSeparatorLine />

            <div className={classes.container}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Language parameters Card */}
                    <div className={classes.card}>
                        <div className={classes.settingsSection}>
                            <div className={classes.titleFlexRow}>
                                <MessagesSquare size={24} className={classes.iconPrimary} />
                                <Text className={classes.sectionTitle}>
                                    {t('settings.languageParameters.title', 'Voice typing')}
                                </Text>
                            </div>
                            <div>
                                <div>
                                    <Text className={classes.sectionSubTitle}>
                                        {t('settings.voice.languageDescription', 'Select the language for voice recognition')}
                                    </Text>
                                </div>
                                <Text className={mergeClasses(classes.sectionDescription, classes.marginBottom2)}>
                                    For best results, select the language you mainly speak. If it&apos;s not listed, it may still be supported via auto-detection.
                                </Text>
                            </div>
                            <Dropdown
                                aria-labelledby="language-dropdown"
                                placeholder="Select language"
                                value={speechToTextSupportedLanguages.find((lang) => lang.locale === selectedLanguage)?.displayLanguage}
                                selectedOptions={[selectedLanguage]}
                                onOptionSelect={(_, data) => {
                                    if (data.optionValue) {
                                        onUpdateUserPreferencesLanguage(data.optionValue);
                                    }
                                }}
                                className={classes.dropdownHeader}
                                button={{ className: classes.dropdownHeaderButton }}
                            >
                                {speechToTextSupportedLanguages.map((language) => (
                                    <Option
                                        key={language.locale}
                                        value={language.locale}
                                        text={language.displayLanguage || language.locale || ''}
                                        className={classes.dropdownOptions}
                                    >
                                        {language.displayLanguage}
                                    </Option>
                                ))}
                            </Dropdown>
                            
                            <div>
                                <div>
                                    <Text className={classes.sectionSubTitle}>
                                        {t('settings.voice.entryType', 'Send Options')}
                                    </Text>
                                </div>
                                {voiceEntryType === 'Automatic Send' && (
                                    <Text className={mergeClasses(classes.sectionDescription, classes.marginBottom1)}>
                                        {t('settings.voice.automaticSendDescription', 'You have a maximum of 30 seconds to record your voice message. The recording will be sent automatically at the end of a silence time period.')}
                                    </Text>
                                )}
                                {voiceEntryType === 'Click Send' && (
                                    <Text className={mergeClasses(classes.sectionDescription, classes.marginBottom1)}>
                                        {t('settings.voice.clickSendDescription', 'You have a maximum of 30 seconds to record your voice message. You can send, pause, cancel, or edit your recording in the prompt textbox before sending.')}
                                    </Text>
                                )}
                            </div>
                            <div>
                                <Dropdown
                                    aria-labelledby="voice-entry-type-dropdown"
                                    placeholder="Select entry type"
                                    value={voiceEntryTypes.find((type) => type.key === voiceEntryType)?.text}
                                    selectedOptions={[voiceEntryType]}
                                    onOptionSelect={(_, data) => {
                                        if (data.optionValue) {
                                            onUpdateVoiceEntryType(data.optionValue);
                                        }
                                    }}
                                    className={classes.dropdownHeader}
                                    button={{ className: classes.dropdownHeaderButton }}
                                >
                                    {voiceEntryTypes.map((type) => (
                                        <Option
                                            key={type.key}
                                            value={type.key}
                                            className={classes.dropdownOptions}
                                        >
                                            {type.text}
                                        </Option>
                                    ))}
                                </Dropdown>
                                {voiceEntryType === 'Automatic Send' && (
                                    <div className={classes.marginTopAutomaticSend}>
                                        <div>
                                            <Text className={classes.sectionSubTitle}>
                                                {t('settings.voice.autoSendNumSecs', 'Number of Seconds until Auto-Send')}
                                            </Text>
                                        </div>
                                        <Text className={classes.sectionDescription}>
                                            {t('settings.voice.autoSendNumSecsDescription', 'How many seconds to wait before automatically sending the voice message')}
                                        </Text>
                                        <div>
                                            <Input
                                                type="number"
                                                value={autoSendNumSecsInput}
                                                min={1}
                                                max={5}
                                                className={classes.inputAutoSend}
                                                style={{ borderColor: parseInt(autoSendNumSecsInput) > 5 ? tokens.colorPaletteRedForeground3 : undefined, borderWidth: parseInt(autoSendNumSecsInput) > 5 ? '2px' : undefined }}
                                                onChange={(_, data) => {
                                                    const value = data.value;
                                                    setAutoSendNumSecsInput(value);
                                                    if (validateAutoSendNumSecs(value)) {
                                                        const numValue = parseInt(value);
                                                        onUpdateAutoSendNumSecs(numValue);
                                                    }
                                                }}
                                                aria-label="Number of seconds until auto-send"
                                            />
                                        </div>
                                        {(autoSendNumSecsError || parseInt(autoSendNumSecsInput) > 5) && (
                                            <Text className={classes.errorText}>
                                                {parseInt(autoSendNumSecsInput) > 5 ? `Maximum value is 5 seconds` : autoSendNumSecsError}
                                            </Text>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Language selection visibility setting removed - language selection is no longer shown in the prompt */}
                        </div>
                    </div>
                    {/* Remove Conversations Card */}
                    <div className={classes.card}>
                        <div className={classes.settingsSection}>
                            <div className={classes.titleFlexRow}>
                                <AlertTriangle size={24} className={classes.iconPrimary} />
                                <Text className={classes.sectionTitle}>
                                    {t('settings.removeConversations.title', 'Remove chats')}
                                </Text>
                            </div>
                            <div>
                                <div>
                                    <Text className={classes.sectionSubTitle}>
                                        {t('settings.clearConversations.clearConversationsSubTitle', 'Clear all chats')}
                                    </Text>
                                </div>
                                <Text className={mergeClasses(classes.sectionDescription, classes.marginClearAllConversationsDescription)}>
                                    {t('settings.clearConversations.clearConversationsDescription', 'This action cannot be undone, this will erase all your SyGPT chats.')}
                                </Text>
                            </div>
                            <div>
                                <SyButton onClick={handleClearConversations} appearance="primary" disabled={isClearingConversations}>
                                    {t('settings.clearConversations.clearAllConversationsButton', 'Clear all chats')}
                                </SyButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConversationSettingsView;